// Lib

// Hooks
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Graph
import {
  createOrganizationMutation,
  organizationShippingDetailsCreateMutation,
} from '@graphql/mutations/organization';

// Actions
import { ClosePopup } from '@actions/popup';
import { SelectOrganization, UpdateOrganizations } from '@actions/organization';

// Api
import client from '@api';
import { ErrorCategory, handleError } from '@services/errorService';
import { applyActiveOrgToken } from '@services/sessionToken';

type RoleType = 'brand' | 'creator' | '';

type SizeType = 'solo' | '2-5' | '5-20' | '21-100' | '100+' | '';

type OrganizationSizeType =
  | 'startup'
  | 'small'
  | 'medium'
  | 'large'
  | 'enterprise';

const getSize: Record<string, OrganizationSizeType> = {
  solo: 'startup',
  '2-5': 'small',
  '5-20': 'medium',
  '21-100': 'large',
  '100+': 'enterprise',
};

export interface OrganizationAnswers {
  name: string;
  website: string;
  size: SizeType;
  industry: string;
  industryOwn: string;
  extraInfo: string;
  goals: string[];
  goalsOwn: string;
  email: string;
  country: string;
  state: string;
  city: string;
  postalCode: string;
  fullName: string;
  streetAddress?: string;
  description?: string;
}

export interface OrganizationErrors {
  name: string;
  website: string;
  size: string;
  industry: string;
  industryOwn: string;
  extraInfo: string;
  goals: string;
  email: string;
  country: string;
  state: string;
  city: string;
  postalCode: string;
  fullName: string;
  streetAddress?: string;
  goalsOwn?: string;
}

interface UseCreateOrganizationReturn {
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  role: RoleType;
  selectedRole: RoleType;
  setSelectedRole: React.Dispatch<React.SetStateAction<RoleType>>;
  loading: boolean;
  answers: OrganizationAnswers;
  error: OrganizationErrors;
  onCreateOrganization: () => Promise<void>;
  onCreateCreatorOrganization: () => Promise<void>;
  onChange: (name: string, value: string | string[]) => void;
  onSetError: (name: string, value: string) => void;
  setRole: React.Dispatch<React.SetStateAction<RoleType>>;
  closeModal: () => void;
}

const INITIAL_ANSWERS: OrganizationAnswers = {
  name: '',
  website: '',
  size: '',
  industry: '',
  industryOwn: '',
  extraInfo: '',
  goals: [],
  goalsOwn: '',
  email: '',
  country: '',
  state: '',
  city: '',
  postalCode: '',
  fullName: '',
  streetAddress: '',
};

const INITIAL_ERROR: OrganizationErrors = {
  name: '',
  website: '',
  size: '',
  industry: '',
  industryOwn: '',
  extraInfo: '',
  goals: '',
  email: '',
  country: '',
  state: '',
  city: '',
  postalCode: '',
  fullName: '',
  streetAddress: '',
};

const buildGoalsField = (
  answers: OrganizationAnswers,
): { goals: string[] } | Record<string, never> => {
  if (answers?.goals?.length) {
    return {
      goals: [
        ...answers.goals,
        ...(answers?.goalsOwn ? [answers.goalsOwn] : []),
      ],
    };
  }
  if (answers?.goalsOwn) return { goals: [answers.goalsOwn] };
  return {};
};

const buildBrandOrgInput = (role: RoleType, answers: OrganizationAnswers) => ({
  type: role,
  ...(answers?.name ? { name: answers.name } : {}),
  ...(answers?.website ? { website: answers.website } : {}),
  ...(answers?.size ? { size: getSize[answers.size] } : {}),
  ...(answers?.industry ? { industry: answers.industry } : {}),
  ...(answers?.industryOwn ? { industry: answers.industryOwn } : {}),
  ...buildGoalsField(answers),
  ...(answers?.description ? { description: answers.description } : {}),
});

const buildShippingDetailsInput = (answers: OrganizationAnswers) => ({
  ...(answers?.email ? { email: answers.email } : {}),
  ...(answers?.country ? { country: answers.country } : {}),
  ...(answers?.state ? { state: answers.state } : {}),
  ...(answers?.city ? { city: answers.city } : {}),
  ...(answers?.postalCode ? { postalCode: answers.postalCode } : {}),
  ...(answers?.fullName ? { fullName: answers.fullName } : {}),
  ...(answers?.streetAddress ? { streetAddress: answers.streetAddress } : {}),
});

const hasShippingDetails = (answers: OrganizationAnswers): boolean =>
  Boolean(
    answers?.email ||
      answers?.country ||
      answers?.postalCode ||
      answers?.fullName,
  );

const getCreatorRedirectPath = (_orgType?: string): string => '/home';

export const useCreateOrganization = (): UseCreateOrganizationReturn => {
  const [step, setStep] = useState<number>(0);
  const [role, setRole] = useState<RoleType>('');
  const [selectedRole, setSelectedRole] = useState<RoleType>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [answers, setAnswers] = useState<OrganizationAnswers>(INITIAL_ANSWERS);
  const [error, setError] = useState<OrganizationErrors>(INITIAL_ERROR);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onChange = useCallback((name: string, value: string | string[]) => {
    setAnswers((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onSetError = useCallback((name: string, value: string) => {
    setError((prev) => ({ ...prev, [name]: value }));
  }, []);

  const closeModal = useCallback(() => {
    dispatch(ClosePopup());
  }, [dispatch]);

  const onCreateOrganization = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await client().mutate({
        mutation: createOrganizationMutation,
        variables: { input: buildBrandOrgInput(role, answers) },
      });

      if (data?.createOrganization) {
        dispatch(UpdateOrganizations(data.createOrganization));
      }

      setLoading(false);
      closeModal();
    } catch (error) {
      handleError(error, { category: ErrorCategory.GRAPHQL, silent: true });
      setLoading(false);
    }
  }, [answers, dispatch, closeModal, role]);

  const onCreateCreatorOrganization = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await client().mutate({
        mutation: createOrganizationMutation,
        variables: {
          input: {
            type: role,
            ...(answers?.name ? { name: answers.name } : {}),
          },
        },
      });

      const created = data?.createOrganization;
      if (created) {
        dispatch(UpdateOrganizations(created));
        dispatch(SelectOrganization(created));
        if (created?.token) applyActiveOrgToken(created.token);
      }

      if (hasShippingDetails(answers)) {
        await client().mutate({
          mutation: organizationShippingDetailsCreateMutation,
          variables: { input: buildShippingDetailsInput(answers) },
        });
      }

      navigate(getCreatorRedirectPath(created?.type), {
        state: { orgSwitchKey: created?._id },
      });
      setLoading(false);
      closeModal();
    } catch (error) {
      handleError(error, { category: ErrorCategory.GRAPHQL, silent: true });
      setLoading(false);
    }
  }, [answers, dispatch, closeModal, role, navigate]);

  return {
    step,
    setStep,
    role,
    selectedRole,
    setSelectedRole,
    loading,
    answers,
    error,
    onCreateOrganization,
    onCreateCreatorOrganization,
    onChange,
    onSetError,
    setRole,
    closeModal,
  };
};
